import { useEffect, useRef, useState } from "react";
import { markerUrl, MARKER_GROUPS } from "@/data/markers.ts";
import { WEAPON_PROFILES } from "@/engine/range.ts";
import type { MarkerKind } from "@/session/schemas.ts";

export interface PendingMarker {
  readonly kind: MarkerKind;
  /** Set only for an artillery marker, so its range rings can be drawn. */
  readonly weaponId?: string;
  /** True for enemy-position markers — drawn as a threat, not friendlies. */
  readonly threat?: boolean;
}

interface MarkerPaletteProps {
  /** Non-null while waiting for a map click to place this marker. */
  readonly placing: PendingMarker | null;
  readonly onPick: (marker: PendingMarker) => void;
  readonly onCancel: () => void;
}

/**
 * Where a marker is born.
 *
 * One tap picks what, the next tap on the map says where — the same two-tap
 * shape as an order, and for the same reason: nobody composes anything while
 * they are being shot at, they just point at what they saw.
 */
export function MarkerPalette({
  placing,
  onPick,
  onCancel,
}: MarkerPaletteProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  useEffect(() => {
    // Cancelling placement (Escape, on the map) should not leave the palette
    // hanging open behind it.
    if (!placing) setOpen(false);
  }, [placing]);

  if (placing) {
    return (
      <button
        type="button"
        onClick={onCancel}
        title="Cancel placing a marker (Escape)"
        className="flex items-center gap-1.5 rounded border border-accent bg-accent-muted px-2 py-1 text-xs font-medium text-accent"
      >
        Click the map to place
        <span className="text-[10px]">Esc</span>
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        title="Drop a marker on the map"
        className={
          open
            ? "rounded border border-border-strong bg-surface-raised px-2.5 py-1 text-xs text-text-primary"
            : "rounded border border-transparent px-2.5 py-1 text-xs text-text-muted transition-colors hover:text-text-secondary"
        }
      >
        Mark
      </button>

      {open && (
        <div className="absolute top-full right-0 z-20 mt-1 w-64 rounded border border-border-strong bg-surface-raised p-2 shadow-lg">
          {MARKER_GROUPS.map((group) => (
            <div key={group.id} className="mb-2 last:mb-0">
              <div className="mb-1 text-[10px] font-medium tracking-wide text-text-muted uppercase">
                {group.label}
              </div>
              <div className="flex flex-wrap gap-1">
                {group.choices.map((choice) =>
                  // Artillery carries a weapon, so its range rings are worth
                  // something — asking which one is one extra tap, not a form.
                  choice.icon === "artillery" ? (
                    WEAPON_PROFILES.map((weapon) => (
                      <button
                        key={`${choice.threat ? "t-" : ""}${weapon.id}`}
                        type="button"
                        title={
                          choice.threat ? `${choice.label} (${weapon.label})` : weapon.label
                        }
                        onClick={() =>
                          onPick({
                            kind: choice.icon,
                            weaponId: weapon.id,
                            ...(choice.threat ? { threat: true } : {}),
                          })
                        }
                        className={`touch-target flex items-center justify-center rounded border transition-colors hover:border-accent ${choice.threat ? "border-danger/40" : "border-border-subtle"}`}
                      >
                        <img
                          src={markerUrl(choice.icon)}
                          alt={choice.threat ? choice.label : weapon.label}
                          width={20}
                          height={20}
                          className={choice.threat ? "hue-rotate-180" : undefined}
                        />
                      </button>
                    ))
                  ) : (
                    <button
                      key={`${choice.threat ? "t-" : ""}${choice.icon}`}
                      type="button"
                      title={choice.label}
                      onClick={() =>
                        onPick({
                          kind: choice.icon,
                          ...(choice.threat ? { threat: true } : {}),
                        })
                      }
                      className={`touch-target flex items-center justify-center rounded border transition-colors hover:border-accent ${choice.threat ? "border-danger/40" : "border-border-subtle"}`}
                    >
                      <img
                        src={markerUrl(choice.icon)}
                        alt={choice.label}
                        width={20}
                        height={20}
                        className={choice.threat ? "hue-rotate-180" : undefined}
                      />
                    </button>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
